import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { User } from '../../../../core/user/user.types';
import { AuthService } from '../../../../core/auth/auth.service';
import { UserService } from '../../../../core/user/user.service';
import { AuthUtils } from '../../../../core/auth/auth.utils';
import { Utils } from '../../../../shared/utils';

@Component({
    selector: 'dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        RouterOutlet,
        QuickChatComponent,
    ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: any[];
    navigationAppearance: 'default' | 'dense' = 'default';
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private currentURL = '';
    private module = '';

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _userService: UserService,
        private _authUtils: AuthUtils,
        public utils: Utils,
    ) {
        // Get the module slug on init
        this.currentURL = _router.url;
        this.module = this.currentURL.split('/')[1];

        this._router.events.pipe(filter(e => e instanceof NavigationEnd))
            .subscribe((r: NavigationEnd) => {
                // Get the module slug on route change
                this.currentURL = r.url;
                this.module = this.currentURL.split('/')[1];
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                // Set the appropriate navigation based on the current module
                this.navigation = navigation[this.module];
                console.log('DenseLayoutComponent navigation: ', this.navigation);

                this._userService.user$.subscribe(user => {
                    console.log('_userService USER: ', user);

                    // Check if user is not null
                    if (!user) return;

                    const userModules = user.modules.map(module => module);

                    // Function to check if a navigation item should be hidden
                    const shouldHideNavItem = (navItem) => {
                        const moduleId = navItem.module;
                        // console.log('moduleId: ', moduleId);

                        // Check if the module exists in the MODULES array
                        const moduleExists = this._authUtils.MODULES.some(module => module.id === moduleId);
                        // console.log('moduleExists: ', moduleExists);

                        // If the module doesn't exist, it's not hidden (visible by default)
                        if (!moduleExists) return false;

                        // Check if the user has access to the module
                        const userHasAccess = userModules.includes(moduleId);
                        // console.log('userHasAccess: ', userHasAccess);
                        // console.log('-----====================-----');

                        return !userHasAccess;
                    };

                    // Iterate over navigation items and set visibility
                    this.navigation.forEach(navEl => {
                        navEl.hidden = () => shouldHideNavItem(navEl);

                        if (navEl.children) {
                            navEl.children.forEach(child => {
                                child.hidden = () => shouldHideNavItem(child);
                            });
                        }
                    });

                    // Trigger change detection
                    this.navigation = [...this.navigation];

                    // Refresh the navigation component
                    const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
                    navComponent?.refresh();
                });

                // console.log("===================================");
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name,
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }
}
